'use client';

import { useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { getOfferingsBatch } from 'services/offerings';
import useGetCaplightPricing from './useCaplightPricing';
import { getElasticOfferingSearch } from 'services/elastic-search';
import { getPageData, getCollectionData, fetchStrapiCustomRoute } from 'utils/lib/strapi';
import type {
  HomepageV2ConfigAttributes,
  HomepageV2Placement,
  HomepageV2OfferingList,
  HomepageV2PageContent,
  FeaturedOfferingConfig,
  PreipoOfferingOverride,
  Se3HeroSlots,
} from 'typescriptUtils/types/homepageV2Config';
import { buildSe3HeroSlot } from 'utils/se3HeroSlots';
import useIsSignedIn from 'hooks/useIsSignedIn';
import type { V2OfferingData as Offering } from 'typescriptUtils/types/offerings/v2';

const DEFAULT_PREIPO_ACTIVE_SLUGS = [
  'omni-fund',
  'omni-fund-ira',
  'grammarly',
  'polymarket-qp-1',
  'amp-robotics',
  'onebrief',
];

const DEFAULTS = {
  showSection: true,
  sectionTitle: 'Pre-IPO deals.',
  sectionSubtitle:
    'Gain exposure to hundreds of the most dynamic, VC-backed companies before they go public.',
  maxActive: 3,
  maxSoldOut: 3,
};

const extractSlugsFromEntries = (
  entries: Array<{ offering_slug: string; is_active: boolean; display_order: number }> | undefined,
): string[] => {
  if (!entries || entries.length === 0) return [];
  return entries
    .filter((o) => o.is_active)
    .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
    .map((o) => o.offering_slug);
};

const applyOverrides = (
  offering: Offering,
  overrideMap: Map<string, PreipoOfferingOverride>,
): Offering => {
  const override = overrideMap.get(offering.slug || '');
  if (!override) return offering;
  return {
    ...offering,
    ...(override.custom_title && { name: override.custom_title }),
    ...(override.custom_tagline && {
      content: override.custom_tagline,
      tagline: override.custom_tagline,
    }),
    ...((override.custom_image?.data?.attributes?.url || override.custom_image?.url) && {
      coverTombstoneUrl: override.custom_image?.data?.attributes?.url || override.custom_image?.url,
    }),
  };
};

/**
 * @param options.skipOfferingsBatch — when true, the pre-IPO offerings batch
 *   query (which falls back to `DEFAULT_PREIPO_ACTIVE_SLUGS` and 404s on
 *   environments missing those offerings) is disabled. The SE3 homepage only
 *   needs the CMS hero slots from this hook and renders its offerings from the
 *   live `/tombstones` endpoint, so it opts out to avoid the wasted requests
 *   and console/network noise (PD-35998).
 */
const useHomepageV2Config = (options?: { skipOfferingsBatch?: boolean }) => {
  const skipOfferingsBatch = options?.skipOfferingsBatch ?? false;
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const isSignedIn: boolean | null = useIsSignedIn();
  const strapiContentId =
    searchParams?.get('strapiContentId') ||
    (queryClient.getQueryData(['strapiContentId']) as string | undefined);

  // Single parallel fetch: all 5 Strapi calls run concurrently via Promise.all
  // (replaces 5 separate useQuery hooks that caused cascading renders)
  const {
    data: aggregatedData,
    isSuccess: strapiSuccess,
    isLoading: aggregatedLoading,
    isError: strapiError,
  } = useQuery({
    queryKey: ['homepageV2ConfigAgg', strapiContentId],
    queryFn: async () => {
      const [
        config,
        retail,
        accredited,
        qp,
        offeringLists,
        retailPageContent,
        accreditedPageContent,
        qpPageContent,
      ] = await Promise.all([
        getPageData({
          type: 'homepage-v2-config',
          preview: Boolean(strapiContentId),
          id: strapiContentId,
          populate: {
            featured_offering: { populate: ['hero_image_desktop', 'hero_image_mobile'] },
            featured_offering_accredited: {
              populate: ['hero_image_desktop', 'hero_image_mobile'],
            },
            featured_offering_qp: { populate: ['hero_image_desktop', 'hero_image_mobile'] },
            preipo_active_offerings_list: true,
            preipo_soldout_offerings_list: true,
            preipo_offerings: { populate: ['custom_image'] },
            explore_banner_image_desktop: true,
            explore_banner_image_mobile: true,
          },
        }).catch(() => null),
        fetchStrapiCustomRoute('/homepage-v2-placements/current/retail').catch(() => null),
        fetchStrapiCustomRoute('/homepage-v2-placements/current/accredited').catch(() => null),
        fetchStrapiCustomRoute('/homepage-v2-placements/current/qp').catch(() => null),
        getCollectionData({
          type: 'homepage-v2-offering-lists',
          populate: 'offering_slugs,preipo_offerings.custom_image',
          pagination: { pageSize: 50 },
        }).catch(() => []),
        fetchStrapiCustomRoute('/homepage-v2-page-contents/current/retail').catch(() => null),
        fetchStrapiCustomRoute('/homepage-v2-page-contents/current/accredited').catch(() => null),
        fetchStrapiCustomRoute('/homepage-v2-page-contents/current/qp').catch(() => null),
      ]);
      return {
        config,
        placements: { retail, accredited, qp },
        offeringLists,
        pageContents: {
          retail: retailPageContent,
          accredited: accreditedPageContent,
          qp: qpPageContent,
        },
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  const strapiData = aggregatedData?.config;
  const config: HomepageV2ConfigAttributes | null = useMemo(() => {
    if (!strapiSuccess || !strapiData) return null;
    return strapiData.attributes || strapiData;
  }, [strapiSuccess, strapiData]);

  const retailPlacement = aggregatedData?.placements?.retail;
  const accreditedPlacement = aggregatedData?.placements?.accredited;
  const qpPlacement = aggregatedData?.placements?.qp;

  const toFeaturedConfig = useCallback(
    (
      p: HomepageV2Placement | null,
      t: 'retail' | 'accredited' | 'qp',
    ): FeaturedOfferingConfig | null => {
      const pAny = p as unknown as { attributes?: HomepageV2Placement };
      const raw = pAny?.attributes || p;
      const fo = (raw as HomepageV2Placement)?.featured_offering;
      if (!fo?.offering_slug) return null;
      return {
        offering_slug: fo.offering_slug,
        investor_type: t,
        custom_title: fo.custom_title,
        custom_tagline: fo.custom_tagline,
        custom_category: fo.custom_category,
        hero_image_desktop: fo.hero_image_desktop,
        hero_image_mobile: fo.hero_image_mobile,
        button_text: fo.button_text,
        query_string: fo.query_string,
        legal_text: fo.legal_text,
        badge_text: fo.badge_text,
        is_active: true,
      };
    },
    [],
  );

  // 3. Unwrap page content from the aggregated query (no separate fetches needed)
  const unwrapPageContent = useCallback((raw: unknown): HomepageV2PageContent | null => {
    if (!raw) return null;
    const entity = raw as { attributes?: HomepageV2PageContent };
    return (entity.attributes || raw) as HomepageV2PageContent;
  }, []);
  const retailPageContent = useMemo(
    () => unwrapPageContent(aggregatedData?.pageContents?.retail),
    [aggregatedData?.pageContents?.retail, unwrapPageContent],
  );
  const accreditedPageContent = useMemo(
    () => unwrapPageContent(aggregatedData?.pageContents?.accredited),
    [aggregatedData?.pageContents?.accredited, unwrapPageContent],
  );
  const qpPageContent = useMemo(
    () => unwrapPageContent(aggregatedData?.pageContents?.qp),
    [aggregatedData?.pageContents?.qp, unwrapPageContent],
  );

  const offeringLists = useMemo(
    () => (aggregatedData?.offeringLists || []) as HomepageV2OfferingList[],
    [aggregatedData?.offeringLists],
  );

  // Derive page-level settings: page-content collection type first, single type fallback, then defaults
  // Use accredited page content as the "default" since it's the most common experience
  const defaultPageContent = accreditedPageContent || retailPageContent;
  const showSection =
    defaultPageContent?.show_preipo_section ?? config?.show_preipo_section ?? DEFAULTS.showSection;
  const sectionTitle =
    defaultPageContent?.preipo_section_title ||
    config?.preipo_section_title ||
    DEFAULTS.sectionTitle;
  const sectionSubtitle =
    defaultPageContent?.preipo_section_subtitle ||
    config?.preipo_section_subtitle ||
    DEFAULTS.sectionSubtitle;
  const maxActive = config?.max_preipo_active_offerings ?? DEFAULTS.maxActive;
  const maxSoldOut = config?.max_preipo_soldout_offerings ?? DEFAULTS.maxSoldOut;

  // Derive slug lists from offering list entries
  const activeSlugs = useMemo(() => {
    const activeList = offeringLists.find((l) => l.section === 'preipo_active');
    const entries = activeList?.offering_slugs || [];
    const fromStrapi = extractSlugsFromEntries(entries);
    return fromStrapi.length > 0 ? fromStrapi : DEFAULT_PREIPO_ACTIVE_SLUGS;
  }, [offeringLists]);

  const soldOutSlugs = useMemo(() => {
    const soldOutList = offeringLists.find((l) => l.section === 'preipo_soldout');
    return extractSlugsFromEntries(soldOutList?.offering_slugs || []);
  }, [offeringLists]);

  // Collect preipo overrides from all offering list entries
  const allPreipoOverrides = useMemo(() => {
    const overrides: PreipoOfferingOverride[] = [];
    for (const list of offeringLists) {
      if (list.preipo_offerings) overrides.push(...list.preipo_offerings);
    }
    return overrides;
  }, [offeringLists]);

  const overrideMap = useMemo(() => {
    const active = allPreipoOverrides.filter((o) => o.is_active);
    return new Map(active.map((o) => [o.offering_slug, o]));
  }, [allPreipoOverrides]);

  const soldOutOverrideSlugs = useMemo(() => {
    return allPreipoOverrides
      .filter((o) => o.is_active && o.show_as_sold_out)
      .map((o) => o.offering_slug);
  }, [allPreipoOverrides]);

  // Derive featured offerings: placement collection type first, single type fallback
  const singleTypeFallback = useMemo(() => {
    if (!config) return { retail: null, accredited: null, qp: null };
    const raw = strapiData?.attributes || strapiData;
    return {
      retail: raw?.featured_offering?.offering_slug
        ? (raw.featured_offering as FeaturedOfferingConfig)
        : null,
      accredited: raw?.featured_offering_accredited?.offering_slug
        ? (raw.featured_offering_accredited as FeaturedOfferingConfig)
        : null,
      qp: raw?.featured_offering_qp?.offering_slug
        ? (raw.featured_offering_qp as FeaturedOfferingConfig)
        : null,
    };
  }, [config, strapiData]);

  const featuredOffering = useMemo<FeaturedOfferingConfig | null>(
    () =>
      toFeaturedConfig(retailPlacement as HomepageV2Placement | null, 'retail') ||
      singleTypeFallback.retail,
    [retailPlacement, toFeaturedConfig, singleTypeFallback],
  );

  const featuredOfferingAccredited = useMemo<FeaturedOfferingConfig | null>(
    () =>
      toFeaturedConfig(accreditedPlacement as HomepageV2Placement | null, 'accredited') ||
      singleTypeFallback.accredited,
    [accreditedPlacement, toFeaturedConfig, singleTypeFallback],
  );

  const featuredOfferingQP = useMemo<FeaturedOfferingConfig | null>(
    () =>
      toFeaturedConfig(qpPlacement as HomepageV2Placement | null, 'qp') || singleTypeFallback.qp,
    [qpPlacement, toFeaturedConfig, singleTypeFallback],
  );

  // Home has one editorial featured offering. The single-type field is the
  // explicit Home CMS configuration; placement records are experience
  // fallbacks retained for legacy V2 pages. Keep this selection centralized so
  // control and every SE3 experience render the exact same Home feature.
  const homepageFeaturedOffering = useMemo<FeaturedOfferingConfig | null>(
    () => singleTypeFallback.retail || featuredOfferingAccredited || featuredOffering || null,
    [singleTypeFallback.retail, featuredOfferingAccredited, featuredOffering],
  );

  const featuredOfferingSlug = featuredOffering?.offering_slug;
  const featuredOfferingAccreditedSlug = featuredOfferingAccredited?.offering_slug;
  const featuredOfferingQPSlug = featuredOfferingQP?.offering_slug;
  const homepageFeaturedOfferingSlug = homepageFeaturedOffering?.offering_slug;

  const heroSlugs = useMemo(() => {
    const slugs: string[] = [];
    if (featuredOfferingSlug) slugs.push(featuredOfferingSlug);
    if (featuredOfferingAccreditedSlug) slugs.push(featuredOfferingAccreditedSlug);
    if (featuredOfferingQPSlug) slugs.push(featuredOfferingQPSlug);
    if (homepageFeaturedOfferingSlug) slugs.push(homepageFeaturedOfferingSlug);
    return Array.from(new Set(slugs));
  }, [
    featuredOfferingSlug,
    featuredOfferingAccreditedSlug,
    featuredOfferingQPSlug,
    homepageFeaturedOfferingSlug,
  ]);

  const nonHeroSlugs = useMemo(() => {
    const heroSet = new Set(heroSlugs);
    const activeFiltered = activeSlugs.filter((s) => !heroSet.has(s)).slice(0, maxActive);
    const soldOutFiltered = soldOutSlugs.filter((s) => !heroSet.has(s)).slice(0, maxSoldOut);
    return Array.from(new Set([...activeFiltered, ...soldOutFiltered]));
  }, [activeSlugs, soldOutSlugs, heroSlugs, maxActive, maxSoldOut]);

  const allSlugsToFetch = useMemo(() => {
    return Array.from(new Set([...heroSlugs, ...nonHeroSlugs]));
  }, [heroSlugs, nonHeroSlugs]);

  const defaultSlugsForPrefetch = useMemo(() => {
    return Array.from(new Set([...DEFAULT_PREIPO_ACTIVE_SLUGS]));
  }, []);

  const slugsToFetch = allSlugsToFetch.length > 0 ? allSlugsToFetch : defaultSlugsForPrefetch;

  const { data: offeringsBatchMap, isLoading: batchLoading } = useQuery({
    queryKey: ['homepageV2OfferingsBatch', slugsToFetch],
    queryFn: () => getOfferingsBatch(slugsToFetch),
    enabled: !skipOfferingsBatch && slugsToFetch.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const fetchedOfferings = useMemo(() => {
    if (!offeringsBatchMap) return [];
    return Array.from(offeringsBatchMap.values()) as Offering[];
  }, [offeringsBatchMap]);
  const fetchLoading = batchLoading;

  const allOfferings = useMemo(() => {
    const map = new Map<string, Offering>();
    for (const o of fetchedOfferings || []) {
      if (o?.slug) map.set(o.slug, o);
    }
    return map;
  }, [fetchedOfferings]);

  const offeringIds = useMemo(() => {
    const ids: string[] = [];
    for (const o of Array.from(allOfferings.values())) {
      if (o.id) {
        ids.push(String(o.id));
      }
    }
    return ids;
  }, [allOfferings]);

  const { pricingMap: caplightPricing } = useGetCaplightPricing(offeringIds, isSignedIn === true);

  const enrichWithCaplight = useCallback(
    (offering: Offering): Offering => {
      if (!offering.id) return offering;
      const pricing = caplightPricing.get(String(offering.id));
      if (!pricing) return offering;
      return {
        ...offering,
        last_round_price: pricing.lastRoundPrice,
        last_round_valuation: pricing.lastRoundValuation,
        last_round_date: pricing.lastRoundDate,
      };
    },
    [caplightPricing],
  );

  const hasActiveConfig = offeringLists.some(
    (l) => l.section === 'preipo_active' && (l.offering_slugs?.length ?? 0) > 0,
  );

  const { data: approvedRegDOfferings } = useQuery({
    queryKey: ['homepageV2ApprovedRegD'],
    queryFn: async () => {
      try {
        const result = await getElasticOfferingSearch({
          query: '',
          statuses: 'APPROVED',
          page: 0,
          size: maxActive,
          regulationTypes: ['Reg D'],
          categoryLabel: undefined,
          sortBy: undefined,
          fingerprint: undefined,
          partners: undefined,
        });
        return result?.list || [];
      } catch {
        return [];
      }
    },
    enabled: !hasActiveConfig,
  });

  const hasSoldOutConfig = soldOutSlugs.length > 0 || soldOutOverrideSlugs.length > 0;

  const { data: closedRegDOfferings } = useQuery({
    queryKey: ['homepageV2ClosedRegD'],
    queryFn: async () => {
      try {
        const result = await getElasticOfferingSearch({
          query: '',
          statuses: 'CLOSED',
          page: 0,
          size: maxSoldOut,
          regulationTypes: ['Reg D'],
          categoryLabel: undefined,
          sortBy: undefined,
          fingerprint: undefined,
          partners: undefined,
        });
        return result?.list || [];
      } catch {
        return [];
      }
    },
    enabled: !hasSoldOutConfig,
  });

  const activeOfferings = useMemo(() => {
    const isClosed = (o: Offering) => o.status?.toLowerCase() === 'closed';
    const isSoldOutOverride = (o: Offering) => soldOutOverrideSlugs.includes(o.slug || '');

    const fromSlugs = activeSlugs
      .map((slug) => allOfferings.get(slug))
      .filter((o): o is Offering => o != null && !isClosed(o) && !isSoldOutOverride(o))
      .map((o) => applyOverrides(o, overrideMap))
      .map(enrichWithCaplight)
      .slice(0, maxActive);

    if (fromSlugs.length > 0) return fromSlugs;
    return (approvedRegDOfferings || []).map(enrichWithCaplight).slice(0, maxActive);
  }, [
    activeSlugs,
    allOfferings,
    overrideMap,
    maxActive,
    soldOutOverrideSlugs,
    enrichWithCaplight,
    approvedRegDOfferings,
  ]);

  const soldOutOfferings = useMemo(() => {
    const isSoldOutOverride = (o: Offering) => soldOutOverrideSlugs.includes(o.slug || '');

    const pinned: Offering[] = soldOutSlugs
      .map((slug) => allOfferings.get(slug))
      .filter((o): o is Offering => o != null);

    const overrideSoldOut = Array.from(allOfferings.values()).filter(
      (o) => isSoldOutOverride(o) && !soldOutSlugs.includes(o.slug || ''),
    );

    const fromConfig = [...pinned, ...overrideSoldOut]
      .map((o) => applyOverrides(o, overrideMap))
      .map(enrichWithCaplight)
      .slice(0, maxSoldOut);

    if (fromConfig.length > 0) return fromConfig;
    return (closedRegDOfferings || []).map(enrichWithCaplight).slice(0, maxSoldOut);
  }, [
    soldOutSlugs,
    allOfferings,
    overrideMap,
    maxSoldOut,
    soldOutOverrideSlugs,
    closedRegDOfferings,
    enrichWithCaplight,
  ]);

  const heroSectionTitle =
    defaultPageContent?.hero_section_title || config?.hero_section_title || '';
  const heroSectionSubtitle =
    defaultPageContent?.hero_section_subtitle || config?.hero_section_subtitle || '';
  const featuredOfferingData = useMemo(() => {
    if (!featuredOfferingSlug) return null;
    const offering = allOfferings.get(featuredOfferingSlug);
    if (!offering || offering.status?.toLowerCase() === 'closed') return null;
    return offering;
  }, [featuredOfferingSlug, allOfferings]);
  const featuredOfferingAccreditedData = useMemo(() => {
    if (!featuredOfferingAccreditedSlug) return null;
    const offering = allOfferings.get(featuredOfferingAccreditedSlug);
    if (!offering || offering.status?.toLowerCase() === 'closed') return null;
    return offering;
  }, [featuredOfferingAccreditedSlug, allOfferings]);
  const featuredOfferingQPData = useMemo(() => {
    if (!featuredOfferingQPSlug) return null;
    const offering = allOfferings.get(featuredOfferingQPSlug);
    if (!offering || offering.status?.toLowerCase() === 'closed') return null;
    return offering;
  }, [featuredOfferingQPSlug, allOfferings]);
  const homepageFeaturedOfferingData = useMemo(() => {
    if (!homepageFeaturedOfferingSlug) return null;
    const offering = allOfferings.get(homepageFeaturedOfferingSlug);
    if (!offering || offering.status?.toLowerCase() === 'closed') return null;
    return offering;
  }, [homepageFeaturedOfferingSlug, allOfferings]);

  const bannerText = useMemo(() => {
    // Banner text comes from placement collection type, not page content
    const rP = retailPlacement as unknown as { attributes?: HomepageV2Placement };
    const aP = accreditedPlacement as unknown as { attributes?: HomepageV2Placement };
    const qP = qpPlacement as unknown as { attributes?: HomepageV2Placement };
    const rBanner = (rP?.attributes || (rP as unknown as HomepageV2Placement))?.banner_text;
    const aBanner = (aP?.attributes || (aP as unknown as HomepageV2Placement))?.banner_text;
    const qBanner = (qP?.attributes || (qP as unknown as HomepageV2Placement))?.banner_text;
    return {
      default: rBanner || config?.banner_text || '',
      accredited: aBanner || config?.banner_text_accredited || '',
      qp: qBanner || config?.banner_text_qp || '',
    };
  }, [retailPlacement, accreditedPlacement, qpPlacement, config]);

  const stats = useMemo(
    () => ({
      totalRaised: {
        value:
          defaultPageContent?.stat_total_raised_value ||
          config?.stat_total_raised_value ||
          '$1.4B+',
        label:
          defaultPageContent?.stat_total_raised_label ||
          config?.stat_total_raised_label ||
          'Total Raised',
      },
      investors: {
        value: defaultPageContent?.stat_investors_value || config?.stat_investors_value || null,
        label:
          defaultPageContent?.stat_investors_label || config?.stat_investors_label || 'Community',
      },
      companies: {
        value: defaultPageContent?.stat_companies_value || config?.stat_companies_value || null,
        label:
          defaultPageContent?.stat_companies_label || config?.stat_companies_label || 'Offerings',
      },
    }),
    [defaultPageContent, config],
  );

  const minAmountRaisedThreshold =
    defaultPageContent?.min_amount_raised_threshold ??
    config?.min_amount_raised_threshold ??
    124000;

  const isLoading = aggregatedLoading || (fetchLoading && allSlugsToFetch.length > 0);

  // SE3 defaults to the accredited placement and switches to retail only for a
  // signed-in retail investment account. Keep the two CMS slots independent.
  const heroSlots = useMemo<Se3HeroSlots>(
    () => ({
      default: buildSe3HeroSlot(featuredOfferingAccredited, 'default', 'accredited'),
      retail: buildSe3HeroSlot(featuredOffering, 'retail', 'retail'),
    }),
    [featuredOfferingAccredited, featuredOffering],
  );

  // PD-35962 — distinguish "still loading" from "published-empty". A null slot
  // is only meaningful once the underlying CMS data has resolved; until then
  // consumers should treat hero slots as pending (not absent) so they don't
  // lock in the demoData fallback for analytics/attribution mid-load.
  const heroSlotsReady = !isLoading;

  return {
    heroSlots,
    heroSlotsReady,
    showSection,
    sectionTitle,
    sectionSubtitle,
    activeOfferings,
    soldOutOfferings,
    isLoading,
    isConfigLoaded: strapiSuccess,
    isError: strapiError,
    featuredOffering,
    homepageFeaturedOffering,
    homepageFeaturedOfferingData,
    featuredOfferingData,
    featuredOfferingAccredited,
    featuredOfferingAccreditedData,
    featuredOfferingQP,
    featuredOfferingQPData,
    heroSectionTitle,
    heroSectionSubtitle,
    bannerText,
    stats,
    minAmountRaisedThreshold,
    // Per-experience page content for components that need experience-specific settings
    pageContent: {
      retail: retailPageContent,
      accredited: accreditedPageContent,
      qp: qpPageContent,
    },
    // Proxy explore data for backward compatibility (SearchDropdown uses exploreFeaturedFunds)
    exploreFeaturedFunds: {
      show: false,
      title: '',
      subtitle: '',
      slugs: [] as string[],
      offerings: [] as Offering[],
    },
    exploreNewPreIPO: {
      show: false,
      title: '',
      subtitle: '',
      slugs: [] as string[],
      offerings: [] as Offering[],
    },
    exploreBanner: { show: false },
  };
};

export default useHomepageV2Config;
